import type { Animal } from "../animals";
import { nameStep } from "../handwriting";

// An ORIGINAL kawaii ice cream (Easy / level 5) — our own linework, not a trace
// of any tutorial. A white domed scoop with a scalloped "melting" bottom sitting
// on a golden waffle cone, a happy face (big sparkly eyes, smile, rosy cheeks)
// and a scatter of rainbow sprinkles up top. The scoop is white (paper); the
// cone, cheeks, eyes and sprinkles colour in.

const OUTLINE = "#2b2b2b";
const CONE = "#e7a33e";
const CONE_LINE = "#c07f27";
const EYE = "#232323";
const CHEEK = "#ff9ec4";
const S_PINK = "#ff6fae";
const S_GREEN = "#5fd07a";
const S_BLUE = "#4bc3e8";
const WHITE = "#ffffff";

const ell = (cx: number, cy: number, rx: number, ry: number) =>
  `M ${cx - rx},${cy} a ${rx},${ry} 0 1,0 ${2 * rx},0 a ${rx},${ry} 0 1,0 ${-2 * rx},0`;

// A rotated little oval (for sprinkles), θ in degrees.
function oval(cx: number, cy: number, rx: number, ry: number, deg = 0) {
  const r = (deg * Math.PI) / 180;
  const dx = rx * Math.cos(r), dy = rx * Math.sin(r);
  const sx = (cx - dx).toFixed(1), sy = (cy - dy).toFixed(1);
  const ex = (cx + dx).toFixed(1), ey = (cy + dy).toFixed(1);
  return `M ${sx},${sy} A ${rx},${ry} ${deg} 1 0 ${ex},${ey} A ${rx},${ry} ${deg} 1 0 ${sx},${sy}`;
}

// --- scoop: domed top, scalloped "melting" bottom -------------------------
const SCOOP =
  "M 60,90 Q 52,86 54,74 Q 54,46 78,30 Q 100,18 122,30 Q 146,46 146,74 " +
  "Q 148,86 140,90 Q 135,98 129,92 Q 123,100 116,93 Q 108,101 100,94 " +
  "Q 92,101 84,93 Q 77,100 71,92 Q 65,98 60,90 Z";

// --- cone: open sides (top edge hidden under the scoop) + closed fill ------
const CONE_OPEN = "M 70,93 L 100,161 L 130,93";
const CONE_FILL = "M 70,93 L 130,93 L 100,161 Z";
const WAFFLE = [
  "M 84,93 L 108,150",
  "M 100,93 L 114,120",
  "M 116,93 L 92,150",
  "M 100,93 L 86,120",
].join(" ");

// --- face -----------------------------------------------------------------
const EYE_L = ell(84, 66, 7, 9.5);
const EYE_R = ell(116, 66, 7, 9.5);
const SPARK_L = ell(81.5, 61.5, 2.6, 2.6);
const SPARK_R = ell(113.5, 61.5, 2.6, 2.6);
const CHEEK_L = ell(72, 77, 6, 3.6);
const CHEEK_R = ell(128, 77, 6, 3.6);
const MOUTH = "M 93,74 Q 100,80 107,74";

// --- sprinkles (rainbow, upper scoop) -------------------------------------
const SP_PINK = [oval(74, 44, 3.6, 1.6, 40), oval(120, 46, 3.6, 1.6, -35), oval(99, 39, 3.6, 1.6, 10)].join(" ");
const SP_GREEN = [oval(90, 37, 3.6, 1.6, -20), oval(110, 52, 3.6, 1.6, 55)].join(" ");
const SP_BLUE = [oval(108, 38, 3.6, 1.6, 45), oval(82, 52, 3.6, 1.6, 15), oval(118, 51, 3.6, 1.6, -25)].join(" ");

export const iceCream: Animal = {
  id: "ice-cream",
  name: "Ice Cream",
  emoji: "🍦",
  viewBox: "0 0 200 200",
  color: OUTLINE,
  level: 5,
  steps: [
    { hint: "Draw the round ice cream scoop 🍦", strokes: [SCOOP] },
    { hint: "Add the pointy cone underneath 🍦", strokes: [CONE_OPEN] },
    { hint: "Draw the waffle lines on the cone 🧇", strokes: [WAFFLE], color: CONE_LINE, strokeWidth: 1.4 },
    { hint: "Draw the big happy eyes 👀", strokes: [EYE_L, EYE_R] },
    { hint: "Add a smile and rosy cheeks 😊", strokes: [CHEEK_L, CHEEK_R, MOUTH] },
    {
      hint: "Now colour your ice cream in! 🖍️",
      strokes: [],
      fills: [
        { d: CONE_FILL, color: CONE },
        { d: CHEEK_L, color: CHEEK },
        { d: CHEEK_R, color: CHEEK },
        { d: SP_PINK, color: S_PINK },
        { d: SP_GREEN, color: S_GREEN },
        { d: SP_BLUE, color: S_BLUE },
        { d: EYE_L, color: EYE },
        { d: EYE_R, color: EYE },
        { d: SPARK_L, color: WHITE },
        { d: SPARK_R, color: WHITE },
      ],
    },
    nameStep("ICE CREAM", { baseline: 190, height: 14 }),
  ],
};
